import { useEffect } from 'react';
import { Box } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';

import { FHButton } from '../../../../../../../../components/base';
import {
  DOCUMENT_UPLOAD_ACCEPTED_IMAGE_TYPES,
  DocumentUploadDialog,
  QuestionnaireSectionLabel,
  useDocumentUpload,
  useDocumentUploadSectionContext,
} from '../../../../../../common';
import { useFormReadOnly } from '../../../../../../../common';
import { Themes } from '../../../../../../../../constants';
import { FORTA_ERROR, FORTA_GRAY3 } from '../../../../../../../../utils/colors';
import InsuranceCardPreview from './InsuranceCardPreview';
import { FInput } from '../../../../../../../../components/baseV2';

import type { InsuranceCardUploadCardProps } from './types';

export default function InsuranceCardUploadCard({
  disabled = false,
  heading,
  illustrationSrc,
  illustrationAlt,
  uploadButtonLabel,
  uploadDialogTitle,
  defaultDocumentTitle,
  documentFileType = '',
  documentPreviewUrl = '',
  previewLoading = false,
  showRequiredError = false,
  uploadedFileName = '',
  existingFileNames = [],
  onUpload,
}: InsuranceCardUploadCardProps) {
  const { isDesktopMobileCaptureHandoff } = useDocumentUploadSectionContext();
  const readOnly = useFormReadOnly();
  const {
    open,
    isDialogOpen,
    closeDialog,
    handleDialogSelect,
    initialFile,
    cameraInputRef,
    handleCameraChange,
  } = useDocumentUpload({ onFile: onUpload });

  useEffect(() => {
    if (readOnly) {
      closeDialog();
    }
  }, [readOnly, closeDialog]);

  if (isDesktopMobileCaptureHandoff && !documentPreviewUrl && !uploadedFileName) {
    return null;
  }

  return (
    <Box
      sx={{
        alignItems: 'center',
        border: `1px solid ${FORTA_GRAY3}`,
        borderRadius: '10px',
        p: 2,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
          <QuestionnaireSectionLabel fontWeight={600} sx={{ alignSelf: 'stretch' }} variant="body1">
            {heading}
          </QuestionnaireSectionLabel>
          {!isDesktopMobileCaptureHandoff && !readOnly && (
            <FHButton
              colorTheme={Themes.Primary}
              disabled={disabled || previewLoading}
              onClick={open}
              startIcon={
                showRequiredError &&
                !disabled &&
                !previewLoading &&
                !uploadedFileName &&
                !documentPreviewUrl ? (
                  <ErrorIcon sx={{ color: FORTA_ERROR }} />
                ) : undefined
              }
            >
              {uploadButtonLabel}
            </FHButton>
          )}
        </Box>
        <InsuranceCardPreview
          documentFileType={documentFileType}
          documentPreviewUrl={documentPreviewUrl}
          illustrationAlt={illustrationAlt}
          illustrationSrc={illustrationSrc}
          previewLoading={previewLoading}
        />
      </Box>
      <FInput
        disabled
        fullWidth
        label="File name"
        name="file_name"
        placeholder="No card image uploaded yet"
        sx={{ mt: 2 }}
        value={uploadedFileName}
      />

      {!isDesktopMobileCaptureHandoff && (
        <>
          <DocumentUploadDialog
            accept={DOCUMENT_UPLOAD_ACCEPTED_IMAGE_TYPES}
            defaultDocumentTitle={defaultDocumentTitle}
            dropzoneHeading="Drag document here"
            dropzoneHint="or use the Browse button to select a document"
            existingFileNames={existingFileNames}
            initialFile={initialFile}
            onClose={closeDialog}
            onSelect={handleDialogSelect}
            open={isDialogOpen && !readOnly}
            subTitle="Document file must be a JPG or PNG file"
            title={uploadDialogTitle}
          />
          <Box
            component="input"
            accept={Object.keys(DOCUMENT_UPLOAD_ACCEPTED_IMAGE_TYPES).join(',')}
            capture="environment"
            onChange={handleCameraChange}
            ref={cameraInputRef}
            sx={{ display: 'none' }}
            type="file"
          />
        </>
      )}
    </Box>
  );
}
